import type { FC, HTMLAttributes } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { Placement } from '@react-types/overlays';
import { ChevronSelectorVertical, Plus } from '@untitledui/icons';
import { useFocusManager } from 'react-aria';
import type { DialogProps as AriaDialogProps } from 'react-aria-components';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Popover as AriaPopover
} from 'react-aria-components';
import { AvatarLabelGroup } from '../../../base/avatar/avatar-label-group';
import { Button } from '../../../base/buttons/button';
import { RadioButtonBase } from '../../../base/radio-buttons/radio-buttons';
import { useBreakpoint } from '../../../../hooks/use-breakpoint';
import { cx } from '../../../../utils/cx';
import { AvatarCompanyIcon } from '../../../base/avatar/base-components';
import type { NavAccountItem } from './types';

type NavAccountType = {
  /** Unique identifier for the nav item. */
  id: string;
  /** Name of the account holder. */
  name: string;
  /** Email address of the account holder. */
  email?: string;
  /** Avatar image URL. */
  avatar?: string;
  /** Online status of the account holder. This is used to display the online status indicator. */
  status?: 'online' | 'offline';
  /** Initials of the account holder name. */
  initials?: string;
  /** Organization logo URL. */
  orgLogoUrl?: string;
  /** Organization name. */
  orgName?: string;
};

export const NavAccountMenu = ({
  link: Link = 'a',
  className,
  selectedAccountId = '',
  showMultipleAccountMenu = false,
  items = [],
  accounts = [],
  onSignOut = () => {},
  ...dialogProps
}: AriaDialogProps & {
  link?: React.ElementType;
  className?: string;
  showMultipleAccountMenu?: boolean;
  items?: NavAccountItem[];
  accounts?: NavAccountType[];
  selectedAccountId?: string;
  onSignOut?: (e: React.FormEvent) => void;
}) => {
  const focusManager = useFocusManager();
  const dialogRef = useRef<HTMLDivElement>(null);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          focusManager?.focusNext({ tabbable: true, wrap: true });
          break;
        case 'ArrowUp':
          focusManager?.focusPrevious({ tabbable: true, wrap: true });
          break;
      }
    },
    [focusManager]
  );

  useEffect(() => {
    const element = dialogRef.current;
    if (element) {
      element.addEventListener('keydown', onKeyDown);
    }

    return () => {
      if (element) {
        element.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [onKeyDown]);

  return (
    <AriaDialog
      {...dialogProps}
      ref={dialogRef}
      className={cx(
        'w-66 rounded-xl bg-primary shadow-lg ring ring-secondary_alt outline-hidden',
        className
      )}
    >
      {showMultipleAccountMenu && (
        <div className="rounded-xl bg-primary ring-1 ring-secondary">
          <div className="flex flex-col gap-0.5 border-t border-secondary py-1.5">
            <div className="px-3 pt-1.5 pb-1 text-xs font-semibold text-tertiary">
              Switch account
            </div>

            <div className="flex flex-col gap-0.5 px-1.5">
              {accounts.map((account) => (
                <button
                  key={account.id}
                  className={cx(
                    'relative w-full cursor-pointer rounded-md px-2 py-1.5 text-left outline-focus-ring hover:bg-primary_hover focus:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
                    account.id === selectedAccountId && 'bg-primary_hover'
                  )}
                >
                  <AvatarLabelGroup
                    status={account.status}
                    size="md"
                    src={account.avatar}
                    title={account.name}
                    subtitle={account.email}
                  />

                  <RadioButtonBase
                    isSelected={account.id === selectedAccountId}
                    className="absolute top-2 right-2"
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2 px-2 pt-0.5 pb-2">
            <Button iconLeading={Plus} color="secondary" size="sm">
              Add account
            </Button>
          </div>
        </div>
      )}

      <div className="pt-1 pb-1.5">
        {items.map((item) =>
          item.href === undefined ? (
            <NavAccountCardMenuItem
              key={`${item.label}-${item.label}`}
              {...item}
            />
          ) : (
            <NavAccountCardMenuItemLink
              link={Link}
              key={`${item.label}-${item.label}`}
              {...item}
            />
          )
        )}
        <form onSubmit={onSignOut}>
          <NavAccountCardMenuItem label="Sign out" />
        </form>
      </div>
    </AriaDialog>
  );
};

const NavAccountCardMenuItemLink = ({
  link: Link = 'a',
  icon: Icon,
  label,
  ...props
}: {
  link?: React.ElementType;
  icon?: FC<{ className?: string }>;
  label: string;
} & HTMLAttributes<HTMLLinkElement>) => {
  return (
    <Link
      {...props}
      className={cx(
        'block group/item w-full cursor-pointer px-1.5 focus:outline-hidden',
        props.className
      )}
    >
      <div
        className={cx(
          'flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover',
          // Focus styles.
          'outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2'
        )}
      >
        <div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
          {Icon && <Icon className="size-5 text-fg-quaternary" />} {label}
        </div>
      </div>
    </Link>
  );
};

const NavAccountCardMenuItem = ({
  icon: Icon,
  label,
  shortcut,
  ...buttonProps
}: {
  icon?: FC<{ className?: string }>;
  label: string;
  shortcut?: string;
} & HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...buttonProps}
      className={cx(
        'group/item w-full cursor-pointer px-1.5 focus:outline-hidden',
        buttonProps.className
      )}
    >
      <div
        className={cx(
          'flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover',
          // Focus styles.
          'outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2'
        )}
      >
        <div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
          {Icon && <Icon className="size-5 text-fg-quaternary" />} {label}
        </div>

        {shortcut && (
          <kbd className="flex rounded px-1 py-px font-body text-xs font-medium text-tertiary ring-1 ring-secondary ring-inset">
            {shortcut}
          </kbd>
        )}
      </div>
    </button>
  );
};

export const NavAccountCard = ({
  popoverPlacement,
  selectedAccountId = '',
  link: Link = 'a',
  avatarGroupLink,
  showMultipleAccountMenu = false,
  accounts = [],
  onSignOut = () => {}
}: {
  popoverPlacement?: Placement;
  selectedAccountId?: string;
  link?: React.ElementType;
  avatarGroupLink?: string;
  showMultipleAccountMenu?: boolean;
  accounts?: NavAccountType[];
  onSignOut?: (e: React.FormEvent) => void;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useBreakpoint('lg');

  const selectedAccount = accounts.find(
    (account) => account.id === selectedAccountId
  );

  const renderAvatarLabelGroup = () => {
    if (!selectedAccount) {
      return null;
    }
    return (
      <AvatarLabelGroup
        size="md"
        src={selectedAccount.avatar}
        title={selectedAccount.name}
        subtitle={selectedAccount.email}
        status={selectedAccount.status}
        initials={selectedAccount.initials}
        badge={
          selectedAccount.orgLogoUrl ? (
            <AvatarCompanyIcon
              src={selectedAccount.orgLogoUrl}
              alt={selectedAccount.orgName}
              size="md"
            />
          ) : null
        }
      />
    );
  };

  if (!selectedAccount) {
    return null;
  }

  return (
    <div
      ref={triggerRef}
      className="relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset"
    >
      {avatarGroupLink ? (
        <Link
          href={avatarGroupLink}
          className="flex min-w-0 flex-1 items-center"
        >
          {renderAvatarLabelGroup()}
        </Link>
      ) : (
        renderAvatarLabelGroup()
      )}

      <div className="absolute top-1.5 right-1.5">
        <AriaDialogTrigger>
          <AriaButton className="flex cursor-pointer items-center justify-center rounded-md p-1.5 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:bg-primary_hover pressed:text-fg-quaternary_hover">
            <ChevronSelectorVertical className="size-4 shrink-0" />
          </AriaButton>
          <AriaPopover
            placement={
              popoverPlacement ?? (isDesktop ? 'right bottom' : 'top right')
            }
            triggerRef={triggerRef}
            offset={8}
            className={({ isEntering, isExiting }) =>
              cx(
                'origin-(--trigger-anchor-point) will-change-transform',
                isEntering &&
                  'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
                isExiting &&
                  'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5'
              )
            }
          >
            <NavAccountMenu
              showMultipleAccountMenu={showMultipleAccountMenu}
              selectedAccountId={selectedAccountId}
              accounts={accounts}
              onSignOut={onSignOut}
            />
          </AriaPopover>
        </AriaDialogTrigger>
      </div>
    </div>
  );
};
